import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { NeonButton, GhostButton } from "@/components/Buttons";
import { Terminal } from "@/components/Terminal";
import { scrollToId } from "@/lib/ui";

function MaskLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yTerm = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const opacityTerm = useTransform(scrollYProgress, [0, 0.85], [1, 0.35]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 sm:pt-40"
    >
      <div className="pointer-events-none absolute -top-40 right-[-15%] h-[560px] w-[560px] rounded-full bg-neon/[0.05] blur-[140px]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neon/40 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-neon"
        >
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-neon" />
          P.E.E. OS™
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-[2.6rem] font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <MaskLine delay={0.2}>Not an indicator.</MaskLine>
          <MaskLine delay={0.34}>
            An <span className="glow-text-neon text-neon">operating system</span>
          </MaskLine>
          <MaskLine delay={0.48}>for trade execution.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-8"
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-mist">
            Position • Event • Entry
          </div>
          <div className="mt-3 font-display text-xl font-bold uppercase tracking-wide text-white sm:text-2xl">
            Execution Over Everything.
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-mist"
        >
          P.E.E. OS is designed to help traders identify market position,
          recognize qualifying events, structure entries, manage risk, validate
          trade conditions, and follow a disciplined execution process.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <NeonButton
            testid="hero-cta-primary"
            onClick={() => scrollToId("pee-os")}
          >
            Explore P.E.E. OS
            <ArrowDown size={16} />
          </NeonButton>
          <GhostButton
            testid="hero-cta-secondary"
            onClick={() => scrollToId("pricing")}
          >
            Join the Community
            <ArrowRight size={16} />
          </GhostButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-dim"
        >
          <span className="h-px w-10 bg-line" />
          Built for Executors.
        </motion.div>

        <div className="mt-16 pb-20 sm:mt-20" style={{ perspective: "1400px" }}>
          <motion.div
            style={{ y: yTerm, opacity: opacityTerm }}
            initial={{ opacity: 0, y: 70, rotateX: 9 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 1.3, delay: 0.65, ease: EASE }}
          >
            <Terminal />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
